import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  messageCatalog,
  type Locale,
} from '@shared/i18n/messages';

const STORAGE_KEY = 'ea_locale';
const LOCALE_QUERY_KEY = 'lang';
const ENGLISH_INTL_LOCALE = 'en-AU';
const HOME_STATS_START_YEAR = 2024;
const HOME_STATS_START_MONTH_INDEX = 0;
const HOME_STAT_MESSAGE_KEYS = new Set([
  'public.home.experienceItem1',
  'public.home.experienceItem2',
  'public.home.reliableItem4',
]);
// base：2024-01 之前已累积的基数；月增长逻辑不变，仅在此叠加基数。
// 校准目标：今天（2026-06）显示 2,500+ / 300+ / 850+。
const HOME_STAT_CONFIG = {
  viewings: { token: 'viewingCount', base: 400, monthly: 70, roundTo: 100 },
  successfulRentals: { token: 'successfulRentalCount', base: 60, monthly: 8, roundTo: 10 },
  servedRenters: { token: 'servedRenterCount', base: 150, monthly: 25, roundTo: 100 },
  localYears: { token: 'localYears', years: true },
} as const;

const textSourceMap = new WeakMap<Text, string>();
const attributeSourceMap = new WeakMap<Element, Record<string, string>>();
const localeListeners = new Set<(locale: Locale) => void>();
const messageLookup = new Map<string, string>();
const messagePatterns: Array<{
  key: string;
  locale: Locale;
  regex: RegExp;
}> = [];

const translatableAttributes = ['placeholder', 'title', 'aria-label', 'alt', 'content', 'value'];

let initialized = false;
let observer: MutationObserver | null = null;
let currentLocale: Locale = DEFAULT_LOCALE;

type InterpolationValues = Record<string, string | number | null | undefined>;
type HomeStatKey = keyof typeof HOME_STAT_CONFIG;

declare global {
  interface Window {
    i18n?: {
      getLocale: () => Locale;
      setLocale: (locale: Locale) => void;
      toggleLocale: () => void;
      t: (idOrLiteral: string, values?: InterpolationValues) => string;
      translatePage: (root?: ParentNode) => void;
      formatDate: (value: string | number | Date, options?: Intl.DateTimeFormatOptions) => string;
      formatDateTime: (
        value: string | number | Date,
        options?: Intl.DateTimeFormatOptions
      ) => string;
      formatCurrency: (amount: number, currency?: string) => string;
    };
  }
}

function normalizeMessageId(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function buildLookupIndex(): void {
  if (messageLookup.size > 0 || messagePatterns.length > 0) {
    return;
  }

  Object.entries(messageCatalog).forEach(([key, descriptor]) => {
    const candidates = [key, descriptor['zh-CN'], descriptor.en];
    candidates.forEach(candidate => {
      const normalized = normalizeMessageId(candidate);
      if (normalized) {
        messageLookup.set(normalized, key);
      }
    });

    (SUPPORTED_LOCALES as Locale[]).forEach(locale => {
      const template = descriptor[locale];
      if (template.includes('{{')) {
        messagePatterns.push({
          key,
          locale,
          regex: compileTemplatePattern(template),
        });
      }
    });
  });
}

function escapeRegex(source: string): string {
  return source.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function compileTemplatePattern(template: string): RegExp {
  const pattern = escapeRegex(template).replace(
    /\\\{\\\{\s*([a-zA-Z0-9_]+)\s*\\\}\\\}/g,
    (_, token: string) => `(?<${token}>[\\s\\S]+?)`
  );
  return new RegExp(`^${pattern}$`);
}

function getCalendarMonthsSinceHomeStatsStart(now: Date = new Date()): number {
  const startMonth = HOME_STATS_START_YEAR * 12 + HOME_STATS_START_MONTH_INDEX;
  const currentMonth = now.getFullYear() * 12 + now.getMonth();
  return Math.max(0, currentMonth - startMonth + 1);
}

function getFullYearsSinceHomeStatsStart(now: Date = new Date()): number {
  let years = now.getFullYear() - HOME_STATS_START_YEAR;
  const beforeAnniversary =
    now.getMonth() < HOME_STATS_START_MONTH_INDEX ||
    (now.getMonth() === HOME_STATS_START_MONTH_INDEX && now.getDate() < 1);

  if (beforeAnniversary) {
    years -= 1;
  }

  return Math.max(0, years);
}

function roundDownTo(value: number, unit: number): number {
  return Math.floor(value / unit) * unit;
}

function getHomeStats(now: Date = new Date()): Record<HomeStatKey, number> {
  const monthCount = getCalendarMonthsSinceHomeStatsStart(now);

  return Object.fromEntries(
    (
      Object.entries(HOME_STAT_CONFIG) as Array<
        [HomeStatKey, (typeof HOME_STAT_CONFIG)[HomeStatKey]]
      >
    ).map(([statKey, config]) => [
      statKey,
      'years' in config
        ? getFullYearsSinceHomeStatsStart(now)
        : roundDownTo(monthCount * config.monthly, config.roundTo) + config.base,
    ])
  ) as Record<HomeStatKey, number>;
}

function getHomeStatsInterpolationValues(): InterpolationValues {
  const stats = getHomeStats();
  const values: InterpolationValues = {};

  (
    Object.entries(HOME_STAT_CONFIG) as Array<[HomeStatKey, (typeof HOME_STAT_CONFIG)[HomeStatKey]]>
  ).forEach(([statKey, config]) => {
    const value = stats[statKey];
    values[config.token] = config.token === 'localYears' ? value : value.toLocaleString('en-US');
  });

  return values;
}

function getDynamicInterpolationValues(key: string): InterpolationValues | undefined {
  return HOME_STAT_MESSAGE_KEYS.has(key) ? getHomeStatsInterpolationValues() : undefined;
}

function getMergedInterpolationValues(
  key: string,
  values?: InterpolationValues
): InterpolationValues | undefined {
  const dynamicValues = getDynamicInterpolationValues(key);
  if (!dynamicValues) {
    return values;
  }

  return {
    ...dynamicValues,
    ...values,
  };
}

function isHomeStatKey(value: string | undefined): value is HomeStatKey {
  return Boolean(value && value in HOME_STAT_CONFIG);
}

function updateHomeStatCounters(): void {
  const stats = getHomeStats();

  document.querySelectorAll<HTMLElement>('[data-home-stat]').forEach(element => {
    const statKey = element.dataset.homeStat;
    if (!isHomeStatKey(statKey)) {
      return;
    }

    element.dataset.count = String(stats[statKey]);
  });
}

function resolveMessagePattern(
  idOrLiteral: string
): { key: string; values: Record<string, string> } | undefined {
  for (const matcher of messagePatterns) {
    const match = idOrLiteral.match(matcher.regex);
    if (match?.groups) {
      return {
        key: matcher.key,
        values: match.groups,
      };
    }
  }

  return undefined;
}

function resolveMessageKey(idOrLiteral: string): string | undefined {
  if (messageCatalog[idOrLiteral]) {
    return idOrLiteral;
  }

  return messageLookup.get(normalizeMessageId(idOrLiteral));
}

function interpolate(template: string, values?: InterpolationValues): string {
  if (!values) {
    return template;
  }

  return template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, token) => {
    const replacement = values[token];
    return replacement === null || replacement === undefined ? '' : String(replacement);
  });
}

function shouldSkipElement(element: Element | null): boolean {
  return Boolean(element?.closest('[data-i18n-skip]'));
}

function getElementAttributeSources(element: Element): Record<string, string> {
  let sources = attributeSourceMap.get(element);
  if (!sources) {
    sources = {};
    attributeSourceMap.set(element, sources);
  }
  return sources;
}

function shouldTranslateAttribute(element: Element, attribute: string): boolean {
  if (attribute !== 'value') {
    return true;
  }

  if (element.tagName !== 'INPUT') {
    return false;
  }

  const type = (element.getAttribute('type') || 'text').toLowerCase();
  return type === 'button' || type === 'submit' || type === 'reset';
}

function translateLiteral(
  idOrLiteral: string,
  locale: Locale = currentLocale,
  values?: InterpolationValues
): string {
  const key = resolveMessageKey(idOrLiteral);
  if (!key) {
    const patternMatch = resolveMessagePattern(idOrLiteral);
    if (!patternMatch) {
      return interpolate(idOrLiteral, values);
    }

    return interpolate(
      messageCatalog[patternMatch.key][locale] || messageCatalog[patternMatch.key][DEFAULT_LOCALE],
      {
        ...patternMatch.values,
        ...getDynamicInterpolationValues(patternMatch.key),
      }
    );
  }

  const descriptor = messageCatalog[key];
  return interpolate(
    descriptor[locale] || descriptor[DEFAULT_LOCALE],
    getMergedInterpolationValues(key, values)
  );
}

function translateTextNode(node: Text): void {
  if (shouldSkipElement(node.parentElement)) {
    return;
  }

  const source = textSourceMap.get(node) ?? node.data;
  if (!textSourceMap.has(node)) {
    textSourceMap.set(node, source);
  }

  if (!source.trim()) {
    return;
  }

  const match = source.match(/^(\s*)([\s\S]*?)(\s*)$/);
  const leading = match?.[1] ?? '';
  const core = match?.[2] ?? source;
  const trailing = match?.[3] ?? '';
  const translated = translateLiteral(core);

  node.data = `${leading}${translated}${trailing}`;
}

function translateElementAttributes(element: Element): void {
  if (shouldSkipElement(element)) {
    return;
  }

  const sources = getElementAttributeSources(element);

  translatableAttributes.forEach(attribute => {
    if (!element.hasAttribute(attribute)) {
      return;
    }

    if (!shouldTranslateAttribute(element, attribute)) {
      return;
    }

    if (!sources[attribute]) {
      sources[attribute] = element.getAttribute(attribute) || '';
    }

    const source = sources[attribute];
    if (!source.trim()) {
      return;
    }

    element.setAttribute(attribute, translateLiteral(source));
  });
}

function translateTree(root: ParentNode): void {
  if (!root) {
    return;
  }

  if (root instanceof Element) {
    translateElementAttributes(root);
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();
  while (textNode) {
    translateTextNode(textNode as Text);
    textNode = walker.nextNode();
  }

  const elements =
    root instanceof Element ? root.querySelectorAll('*') : (root as Document).querySelectorAll('*');

  elements.forEach(element => translateElementAttributes(element));
}

function updateDocumentMetadata(): void {
  document.documentElement.lang = currentLocale;
}

type SwitcherTheme = 'light' | 'dark';

/** 浅色用于后台/用户中心的白底头部，深色用于首页的 night + gold 主题。 */
const SWITCHER_BUTTON_THEMES: Record<SwitcherTheme, { active: string[]; inactive: string[] }> = {
  light: {
    active: ['bg-blue-600', 'text-white', 'shadow-sm'],
    inactive: ['text-gray-600', 'hover:text-blue-600'],
  },
  dark: {
    active: ['bg-gold', 'text-night', 'shadow-sm'],
    inactive: ['text-mist', 'hover:text-white'],
  },
};

const SWITCHER_CONTAINER_THEMES: Record<SwitcherTheme, string> = {
  light: 'border-gray-200 bg-white/90',
  dark: 'border-white/15 bg-white/5',
};

function updateLocaleSwitchers(): void {
  document.querySelectorAll<HTMLElement>('[data-locale-switch]').forEach(button => {
    const locale = button.dataset.localeSwitch as Locale | undefined;
    const isActive = locale === currentLocale;

    const container = button.closest<HTMLElement>('[data-locale-switcher]');
    const theme = (container?.dataset.localeSwitcherTheme as SwitcherTheme) ?? 'light';
    const palette = SWITCHER_BUTTON_THEMES[theme] ?? SWITCHER_BUTTON_THEMES.light;

    palette.active.forEach(cls => button.classList.toggle(cls, isActive));
    palette.inactive.forEach(cls => button.classList.toggle(cls, !isActive));
  });
}

function setupLocaleSwitcherDelegation(): void {
  document.addEventListener('click', event => {
    const target = event.target as HTMLElement;
    const switcher = target.closest('[data-locale-switch]') as HTMLElement | null;
    if (!switcher) {
      return;
    }

    const locale = switcher.dataset.localeSwitch as Locale | undefined;
    if (locale) {
      setLocale(locale);
    }
  });
}

function watchDomChanges(): void {
  if (observer) {
    return;
  }

  observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          translateTextNode(node as Text);
          return;
        }

        if (node.nodeType === Node.ELEMENT_NODE) {
          translateTree(node as Element);
        }
      });
    });

    updateLocaleSwitchers();
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

function getDomainDefaultLocale(hostname: string = window.location.hostname): Locale {
  return hostname.toLowerCase().endsWith('.com.au') ? 'en' : 'zh-CN';
}

function detectInitialLocale(): Locale {
  const url = new URL(window.location.href);
  const localeFromQuery = url.searchParams.get(LOCALE_QUERY_KEY) as Locale | null;
  if (localeFromQuery && SUPPORTED_LOCALES.includes(localeFromQuery)) {
    localStorage.setItem(STORAGE_KEY, localeFromQuery);
    return localeFromQuery;
  }

  const storedLocale = localStorage.getItem(STORAGE_KEY) as Locale | null;
  if (storedLocale && SUPPORTED_LOCALES.includes(storedLocale)) {
    return storedLocale;
  }

  return getDomainDefaultLocale();
}

export function getLocale(): Locale {
  return currentLocale;
}

export function getIntlLocale(locale: Locale = currentLocale): string {
  return locale === 'en' ? ENGLISH_INTL_LOCALE : DEFAULT_LOCALE;
}

export function t(idOrLiteral: string, values?: InterpolationValues): string {
  return translateLiteral(idOrLiteral, currentLocale, values);
}

export function formatDate(
  value: string | number | Date,
  options: Intl.DateTimeFormatOptions = {}
): string {
  return new Intl.DateTimeFormat(getIntlLocale(), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...options,
  }).format(new Date(value));
}

export function formatDateTime(
  value: string | number | Date,
  options: Intl.DateTimeFormatOptions = {}
): string {
  return new Intl.DateTimeFormat(getIntlLocale(), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  }).format(new Date(value));
}

export function formatCurrency(amount: number, currency: string = 'AUD'): string {
  return new Intl.NumberFormat(getIntlLocale(), {
    style: 'currency',
    currency,
  }).format(amount);
}

export function translatePage(root: ParentNode = document): void {
  if (!document.documentElement) {
    return;
  }

  updateHomeStatCounters();
  updateDocumentMetadata();
  translateTree(root);
  updateLocaleSwitchers();
}

export function setLocale(locale: Locale): void {
  if (!SUPPORTED_LOCALES.includes(locale)) {
    return;
  }

  currentLocale = locale;
  localStorage.setItem(STORAGE_KEY, locale);
  translatePage();
  localeListeners.forEach(listener => listener(currentLocale));
  window.dispatchEvent(new CustomEvent('ea:localechange', { detail: { locale } }));
}

export function toggleLocale(): void {
  setLocale(currentLocale === 'zh-CN' ? 'en' : 'zh-CN');
}

export function subscribeToLocaleChange(listener: (locale: Locale) => void): () => void {
  localeListeners.add(listener);
  return () => {
    localeListeners.delete(listener);
  };
}

export function getLanguageSwitcherHTML(
  extraClassName: string = '',
  theme: SwitcherTheme = 'light'
): string {
  const className = extraClassName ? ` ${extraClassName}` : '';
  const containerTheme = SWITCHER_CONTAINER_THEMES[theme] ?? SWITCHER_CONTAINER_THEMES.light;
  return `
    <div class="inline-flex items-center rounded-full border ${containerTheme} p-1 text-xs font-semibold${className}" data-i18n-skip data-locale-switcher data-locale-switcher-theme="${theme}">
      <button
        type="button"
        class="min-h-0 min-w-0 rounded-full px-3 py-1.5 text-xs leading-none transition-colors"
        data-locale-switch="zh-CN"
        aria-label="Switch language to Chinese"
      >
        中
      </button>
      <button
        type="button"
        class="min-h-0 min-w-0 rounded-full px-3 py-1.5 text-xs leading-none transition-colors"
        data-locale-switch="en"
        aria-label="Switch language to English"
      >
        EN
      </button>
    </div>
  `;
}

export function initializeI18n(): void {
  buildLookupIndex();
  currentLocale = detectInitialLocale();

  if (initialized) {
    translatePage();
    return;
  }

  initialized = true;
  setupLocaleSwitcherDelegation();

  const boot = () => {
    translatePage();
    watchDomChanges();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.i18n = {
    getLocale,
    setLocale,
    toggleLocale,
    t,
    translatePage,
    formatDate,
    formatDateTime,
    formatCurrency,
  };
}
